import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {NavBar} from '@components/navigation/NavBar/NavBar';
import {
    VehicleExchangeRequest,
    VehicleExchangeService,
    VehicleExchangeResponse,
    ExchangeType
} from '@services/vehicleExchangeService';
import {useIsAuthenticated, useMsal} from "@azure/msal-react";
import {ConfirmationBox, ErrorBox, WarningBox} from "@components/cards/messageBox";
import Circular from '@components/spinners/Circular/Circular';
import Button from '@components/buttons/Button/Button';
import {useNavigate} from "react-router";
import ExternalLink from "@assets/images/external_link.svg";

export default function VehicleExchangeConfirmation() {
    // Get documentId from the URL params
    const {documentId, country} = useParams();
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [result, setResult] = useState<VehicleExchangeResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (!documentId) {
            console.log('No documentId provided in URL params');
            throw new Error('Document ID is required to fetch vehicle exchange result');
        }
        setLoading(true);
        const request: VehicleExchangeRequest = { exchangeDocumentId: documentId };
        VehicleExchangeService.performVehicleExchange(request, { msalContext, isAuthenticated },)
            .then((res) => {
                setResult(res);
                setLoading(false);
            })
            .catch((err) => {
                setError(err?.message || 'Vehicle exchange failed');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [documentId]);

    const paragraphStyle = {
        marginTop: '1.5vh',
    };


    return (
        <>
            <NavBar pageTitle="Confirmation"/>
            <div style={{marginTop: '1.5vh'}}/>
            {loading ? (
                <div style={{textAlign: 'center', marginTop: '2vh'}}>
                    <Circular testId="exchange-loading-spinner"/>
                </div>
            ) : result ? (
                <>
                    {/*Fleetio*/}
                    {result.fleetio.success_old ? (
                        <>
                            <ConfirmationBox message="Old vehicle unassigned in Fleetio"/>
                            {result.exchange_type === ExchangeType.Permanent ? (
                                <p style={paragraphStyle}>
                                    The following vehicle has been unassigned from the
                                    customer: <b>{result.fleetio.vehicle_description_old}</b>.
                                </p>
                            ) : (
                                <p style={paragraphStyle}>
                                    The following vehicle has been temporarily unassigned from the customer, and will be held reserved for the exchange back
                                </p>
                            )}
                            <a href={result.fleetio.vehicle_url_old} target="_blank" rel="noopener noreferrer">View in Fleetio</a>
                        </>
                    ) : (
                        <>
                            <WarningBox message={error || "Fleetio update failed for old vehicle"}/>
                            <p style={paragraphStyle}>
                                Something went wrong - please check the current assignment
                                for: <b>{result.fleetio.vehicle_description_old}</b>.
                                <a href={result.fleetio.vehicle_url_old} target="_blank" rel="noopener noreferrer">View
                                    in Fleetio</a>
                            </p>
                        </>
                    )}
                    <div style={{marginTop: '1.5vh'}}/>
                    {result.fleetio.success_new ? (
                        <>
                            <ConfirmationBox message="New vehicle assigned in Fleetio"/>
                            <p style={paragraphStyle}>
                                The following vehicle has been assigned to the
                                customer: <b>{result.fleetio.vehicle_description_new}</b>.
                                <a href={result.fleetio.vehicle_url_new} target="_blank" rel="noopener noreferrer">View
                                    in Fleetio</a>
                            </p>
                        </>
                    ) : (
                        <>
                            <WarningBox message={"New vehicle could not be assigned in Fleetio"}/>
                            <p style={paragraphStyle}>
                                Something went wrong - please check the current assignment
                                for: <b>{result.fleetio.vehicle_description_new}</b>.
                                <a href={result.fleetio.vehicle_url_new} target="_blank" rel="noopener noreferrer">View
                                    in Fleetio</a>
                            </p>
                        </>
                    )}
                    {/*Zoho*/}
                    <div style={{marginTop: '1.5vh'}}/>
                    {result.zoho?.success ? (
                        <>
                            <ConfirmationBox message="Zoho subscription updated"/>
                            <p style={paragraphStyle}>
                                The customer’s subscription details have been updated.
                                A signed exchange contract PDF document has been generated and added to the customer
                                subscription. <a href={result.zoho.subscription_url} target="_blank"
                                                 rel="noopener noreferrer">View
                                subscription in Zoho</a>
                            </p>
                        </>
                    ) : (
                        <>
                            <WarningBox message={"Zoho subscription update failed"}/>
                            <p style={paragraphStyle}>
                                Something went wrong - please check the subscription. <a
                                href={result.zoho?.subscription_url} target="_blank" rel="noopener noreferrer">View
                                in Zoho</a>
                            </p>
                        </>
                    )}
                    {/*Chargebee*/}
                    {result.chargebee ? (
                        <>
                            <div style={{marginTop: '1.5vh'}}/>
                            {result.chargebee.success ? (
                                <>
                                    <ConfirmationBox message="Charges raised in Chargebee"/>
                                    <p style={paragraphStyle}>
                                        <b>Unbilled charges</b> have been added to the customer's Chargebee subscription.
                                        Please proceed to Chargebee to generate an invoice.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <WarningBox message={"Chargebee update failed"}/>
                                    {result.chargebee.subscription_url ? (
                                        <p style={paragraphStyle}>
                                            Something went wrong - please check the customer's subscription.
                                        </p>
                                    ) : (
                                        <p style={paragraphStyle}>
                                            Something went wrong - there is no active subscription or customer in Chargebee.
                                            Please check Chargebee.
                                        </p>
                                    )}
                                </>
                            )}
                        </>
                    ):(<></>)}
                    {/* Possible incorrect original vehicle data warning */}
                    {result.possible_incorrect_original_vehicle_data && (
                        <>
                            <div style={{marginTop: '1.5vh'}}/>
                            <ErrorBox message={"Legacy Exchange Detected On Subscription"}/>
                            <p style={paragraphStyle}>
                                This customer subscription has previously had Pre-Smartops exchange. SmartOps may not
                                have the correct data for the customers RTB vehicle. You <b>must</b> navigate to the
                                <a href={result.fleetio.vehicle_url_old} target="_blank" rel="noopener noreferrer">old
                                vehicle</a> in Fleetio. If this vehicle is not the customers permanent RTB vehicle
                                (confirm via contract) then delete the value in the “RTB owned by” field.
                            </p>
                        </>
                    )}
                </>
            ) : (
                <WarningBox message={error || "Something went wrong"}/>
            )}
            {/* Dynamic Button - Chargebee subscription or Customer Overview */}
            <div style={{marginTop: '6vh'}}/>
            {result?.chargebee?.subscription_url ? (
                <Button
                    label="View Subscription in Chargebee"
                    iconSrc={ExternalLink}
                    iconRight={true}
                    onClickFunc={() => {
                        console.debug('Opening Chargebee subscription URL:', result.chargebee!.subscription_url);
                        window.open(result.chargebee!.subscription_url, '_blank', 'noopener,noreferrer');
                    }}
                />
            ) : (
                <Button
                    label="Customer Overview"
                    onClickFunc={() => {
                        if (result?.zoho?.contact_id && country) {
                            console.log(`Navigating to customer overview for contact ID: ${result.zoho.contact_id} in country: ${country}`);
                            navigate(`/customer/${country}/${result.zoho.contact_id}/overview`);
                        } else {
                            console.error('Contact ID or country is missing in the result');
                            navigate('/'); // Fallback to home if contact ID or country is not available
                        }
                    }}
                />
            )}
        </>
    );
};
